import React, { Suspense, lazy } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { SiteLayout } from '@/components/SiteLayout';
import { Spinner } from '@/components/ui';
import { LandingScreen } from '@/screens/Landing';

// Browser history, not hashes — /verifyemail/<uuid> and /newpassword/<uuid> are
// plain paths built by the API (configEnv fixLinks), and public/.htaccess rewrites
// anything that is not a real file to index.html so they resolve on a cold load.
//
// The landing page is the common case and is bundled eagerly. Everything else is
// split: the legal pages are long prose almost nobody opens, and the two auth
// screens additionally pull in the shared errorcode bundle. Neither should be in
// the download a first-time visitor pays for.
const PrivacyScreen = lazy(() => import('@/screens/legal/PrivacyScreen').then((m) => ({ default: m.PrivacyScreen })));
const TermsScreen = lazy(() => import('@/screens/legal/TermsScreen').then((m) => ({ default: m.TermsScreen })));
// Linked from every dunning email, so it has to resolve on a cold load from a
// mail client — public/.htaccess already rewrites unknown paths to index.html,
// which is the only reason /verifyemail works and the only reason this will.
const BillingHelpScreen = lazy(() => import('@/screens/legal/BillingHelpScreen').then((m) => ({ default: m.BillingHelpScreen })));
// Split like the other prose pages, and for one extra reason: it imports the
// sign roll-call from the phone app's creditsContent, which is a few kB of author
// names and sign codes that a first-time visitor to the landing page should not
// be made to download.
const CreditsScreen = lazy(() => import('@/screens/legal/CreditsScreen').then((m) => ({ default: m.CreditsScreen })));
// The 14-day right of withdrawal and the model form. Split like the other prose
// pages, and linked from every order confirmation -- which means it has to
// resolve on a cold load from a mail client, months later, the same way
// /billing-help does.
const WithdrawalScreen = lazy(() => import('@/screens/legal/WithdrawalScreen').then((m) => ({ default: m.WithdrawalScreen })));
// The Art. 28 processing agreement and the sub-processor list it promises notice
// about. Split like the other prose pages, and both pull the agreement text from
// the phone app's dpaContent -- a few kB of contract that a visitor to the
// landing page has no reason to download.
const DpaScreen = lazy(() => import('@/screens/legal/DpaScreen').then((m) => ({ default: m.DpaScreen })));
const SubprocessorsScreen = lazy(() => import('@/screens/legal/SubprocessorsScreen').then((m) => ({ default: m.SubprocessorsScreen })));
// The how-to articles. Split for the same reason as the legal pages and one
// more: it pulls the shared help locale bundle for the visitor's language, which
// somebody who only ever sees the landing page should not download.
const HelpScreen = lazy(() => import('@/screens/help/HelpScreen').then((m) => ({ default: m.HelpScreen })));
const VerifyEmailScreen = lazy(() => import('@/screens/auth/VerifyEmailScreen').then((m) => ({ default: m.VerifyEmailScreen })));
const NewPasswordScreen = lazy(() => import('@/screens/auth/NewPasswordScreen').then((m) => ({ default: m.NewPasswordScreen })));

const deferred = (node: React.ReactNode): React.ReactElement => (
    <Suspense fallback={<Spinner />}>{node}</Suspense>
);

const routes: RouteObject[] = [
    {
        element: <SiteLayout />,
        children: [
            { index: true, element: <LandingScreen /> },

            { path: '/privacy', element: deferred(<PrivacyScreen />) },
            { path: '/terms', element: deferred(<TermsScreen />) },
            { path: '/billing-help', element: deferred(<BillingHelpScreen />) },
            { path: '/withdrawal', element: deferred(<WithdrawalScreen />) },
            // Linked from the terms, from the app's company activation screen and
            // from the privacy policy's controller clause, so it has to resolve on
            // a cold load the way /verifyemail does.
            { path: '/dpa', element: deferred(<DpaScreen />) },
            { path: '/subprocessors', element: deferred(<SubprocessorsScreen />) },
            // Linked from the footer, and the URL the store listings point at for
            // third-party licences — the CC BY-SA sign artwork needs attribution
            // somewhere a person can reach without installing the app first.
            { path: '/credits', element: deferred(<CreditsScreen />) },

            // /help and /help/<articleId>. Both resolve on a cold load from a
            // pasted link because public/.htaccess already rewrites unknown paths
            // to index.html — the same rule that makes /verifyemail work. An
            // unknown articleId renders the index rather than 404ing, so a link
            // from an old support email still lands somewhere useful.
            { path: '/help', element: deferred(<HelpScreen />) },
            { path: '/help/:articleId', element: deferred(<HelpScreen />) },

            // Reached from an email by someone who is not signed in and has no
            // reason to know this is a React app. Moved here from busLineSrv when
            // the console took the admin subdomain; the paths are unchanged, which
            // is why the API's fixLinks needed no edit.
            { path: '/verifyemail/:uuid', element: deferred(<VerifyEmailScreen />) },
            { path: '/newpassword/:uuid', element: deferred(<NewPasswordScreen />) },

            // Unknown path: the site has a handful of pages, so there is nothing
            // useful a 404 could offer that the landing page does not.
            { path: '*', element: <Navigate to="/" replace /> },
        ],
    },
];

export const router = createBrowserRouter(routes);
